import { AxiosService } from '../../axios/axios.service.ts';
import { BASE_URL_STORAGE_SANDBOX } from '../../axios/axios.constants.ts';
import { IS_PRODUCTION } from '../../constants/envs.ts';
import { tAxiosCustomConfig } from '../../axios/axios.types.ts';
import {
  tFunnelLeadLegacy,
  tPipeResponse,
  tUpdateLeadRequest,
} from '../../pages/Pipe/Pipe.types.ts';
import {
  tConversationSummary,
  tCreateSummaryAIRequest,
  tCreateSummaryAIResponse,
  tSummaryEligibilityRequest,
  tSummaryEligibilityResponse,
  tSummaryRequest,
} from '../../types/summary.types.ts';
import {
  DeleteChatEventPayload,
  TemplateFbIg,
  tArchiveResponse,
  tChangeChatState,
  tChangeChatStateRequest,
  tChatData,
  tChatEvent,
  tChatEventResponse,
  tChatLeadsRequest,
  tChatResponse,
  tCreateFollowUpData,
  tCreateFollowUpRequest,
  tCreateFollowUpResponse,
  tCreateLeadsalesAIData,
  tCreateLeadsalesAIRequest,
  tCreateLeadsalesAIResponse,
  tCreateNoteData,
  tCreateNoteRequest,
  tCreateNoteResponse,
  tDeleteEventRequest,
  tDeleteEventResponse,
  tBlockContactResponse,
  tRetryWapiMessageResponse,
  tDeleteFileToS3Request,
  tDeleteTagRequest,
  tFetchTemplatesResponse,
  tForwardEventResponse,
  tForwardRequest,
  tFunnelInfo,
  tGetChatIDResponse,
  tGetChatLeadResponse,
  tGetChatResponse,
  tGetLeadHistoryRequest,
  tGetLeadHistoryResponse,
  tGetLeadsResponse,
  tGetEventsAroundRequest,
  tGetNextPageRequest,
  tGetNextPageResponse,
  tGetParticipants,
  tGetTemplatesResponse,
  tGetWorkspacesInfoResponse,
  tQuickActionPayload,
  tQuickActionResponse,
  tQuickReply,
  tSendMessageRequest,
  tSendMessageResponse,
  tTagDeleteRequest,
  tTagRequest,
  tUpdateEventData,
  tUpdateEventRequest,
  tUpdateEventResponse,
  tUpdateQuickActionPayload,
  tUpdateTagRequest,
  tUpdateTagResponse,
  tUploadFileToS3Request,
  tUploadFileToS3ResponseData,
  tWapiSendInteractiveRequest,
  tWapiSendInteractiveResponse,
  tWapiSendTemplateRequest,
  tWapiSendTemplateResponse,
  tQuickReplyInteractiveConfig,
  tRecordHumanActionBody,
  tRecordHumanActionParams,
} from './Chat.types.ts';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums.ts';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { summaryService } from 'src/services/AI/summary.service.ts';
import { fromQuickReplyInteractiveConfig } from './components/MessageBar/components/InteractiveAction/InteractiveAction.functions.ts';

// Raw row as it may arrive from the quick-replies gateway. Two shapes can
// show up today:
//   (a) `content.interactive` with the flat {type, button|cta_url|list}
//       shape — this is what bff-web PR #648 emits (pass-through from
//       quick-replies-service).
//   (b) `interactive_config` at the top level (snake_case) — the shape
//       quick-replies-service emits if bff-web ever stops mapping it.
// `hydrateQuickReply` normalizes both into `content.interactive` with
// the composer's nested `action` shape so every downstream consumer
// (QuickActionRow pill, picker, composer preload) can use a single
// shape.
type tQuickReplyRaw = Omit<tQuickReply, 'content'> & {
  content?: tQuickReply['content'] & {
    interactive?:
      | tQuickReply['content']['interactive']
      | tQuickReplyInteractiveConfig;
  };
  interactive_config?: tQuickReplyInteractiveConfig;
};

// A composer-shape interactive already has `action`; the flat wire shape
// has one of `button` / `cta_url` / `list` instead. Used to decide
// whether to transform or leave as-is.
function isComposerShapeInteractive(
  value: unknown,
): value is tQuickReply['content']['interactive'] {
  if (!value || typeof value !== 'object') return false;
  return 'action' in value || 'reply' in value;
}

function isFlatWireShapeInteractive(
  value: unknown,
): value is tQuickReplyInteractiveConfig {
  if (!value || typeof value !== 'object') return false;
  return 'button' in value || 'cta_url' in value || 'list' in value;
}

function hydrateQuickReply(raw: tQuickReplyRaw): tQuickReply {
  const rawInteractive = raw.content?.interactive;

  // Already composer-shape (has `action` / `reply`) — no transform.
  if (isComposerShapeInteractive(rawInteractive)) {
    return raw as tQuickReply;
  }

  // Flat wire shape arrived under `content.interactive` (bff-web
  // passthrough) — transform to composer shape.
  if (isFlatWireShapeInteractive(rawInteractive)) {
    const hydrated = fromQuickReplyInteractiveConfig(rawInteractive);
    if (!hydrated) return raw as tQuickReply;
    return {
      ...raw,
      content: {
        ...raw.content,
        interactive: hydrated,
      },
    } as tQuickReply;
  }

  // Flat wire shape arrived at the top level (snake_case) — transform
  // and place under content.interactive.
  const hydrated = fromQuickReplyInteractiveConfig(raw.interactive_config);
  if (!hydrated) return raw as tQuickReply;
  return {
    ...raw,
    content: {
      ...raw.content,
      interactive: hydrated,
    },
  } as tQuickReply;
}

// Rewrites the raw S3 URL the storage service returns
// (`leadsales-storage.s3.amazonaws.com/…`) to our env-aware CDN domain
// (`leadsales.services` in prod, `staging.leadsales.services` elsewhere). Same
// pattern as `AudioRecorderAction.hooks.tsx:94-103` — lifted here so every
// sandbox consumer gets it for free. Centralized so future changes (new env,
// new domain, additional bucket) live in one place.
//
// Note: leads-ai-service's `MessageAttachmentSchema.url.max(2000)` accepts
// both the raw and masked URLs since both are well under the cap. The masking
// is for human-readable URLs in the message bubble + matching the production
// WhatsApp media URL shape leads-ai-service already trusts via SSRF guard.
// Build a fully-qualified URL for sandbox endpoints using
// `VITE_BASE_URL_STORAGE_SANDBOX` when set, otherwise falling back to
// BASE_URL_LEGACY. Lets local-only sandbox testing route to the Vite proxy
// (which forwards to local storage-management-service in Docker) while every
// other isLegacy:true call continues to hit whatever BASE_URL_LEGACY points
// at — so in-flight E2E flows that depend on staging aren't disturbed.
//
// IMPORTANT: this MUST stay absolute. Axios prepends its configured baseURL
// (BASE_URL_LEGACY = staging) to relative paths BEFORE the browser request
// fires, so a relative `/storage/sandbox-*` lands on staging API directly
// and the Vite dev-server proxy never sees it. The local-dev pattern is to
// set VITE_BASE_URL_STORAGE_SANDBOX=https://localhost:3000 so axios sees an
// absolute URL, skips its baseURL, and the request hits the Vite dev server
// where the /storage/sandbox-* proxy rule intercepts and forwards to the
// local storage-management-service container.
function sandboxEndpoint(path: string): string {
  const base = BASE_URL_STORAGE_SANDBOX.replace(/\/$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

// Rewrites the raw S3 URL to the right host for the current environment.
//
// Production: rewrites to `leadsales.services` (the CDN domain).
// Staging:    rewrites to `staging.leadsales.services`.
// Local dev:  rewrites to `http://localstack:4566/leadsales-storage/...`
//   — the Docker-internal hostname leads-ai-service can fetch via Docker DNS.
//   Trade-off: the host browser CANNOT resolve `localstack`, so the message
//   bubble's image preview will fail to load locally. The agent still gets
//   the actual image bytes because leads-ai-service inlines as base64 from
//   inside the Docker network. Deploy to staging for full preview-and-agent
//   E2E. Local mode is detected by `VITE_BASE_URL_STORAGE_SANDBOX` being set.
function maskSandboxStorageUrl(rawUrl: string): string {
  if (!rawUrl) return '';
  const storageDomain = 'leadsales-storage.s3.amazonaws.com';

  if (import.meta.env.VITE_BASE_URL_STORAGE_SANDBOX) {
    return rawUrl.replace(`https://${storageDomain}`, 'http://localstack:4566/leadsales-storage');
  }

  const targetDomain = IS_PRODUCTION ? 'leadsales.services' : 'staging.leadsales.services';
  return rawUrl.replace(storageDomain, targetDomain);
}

export const ChatService = {
  createNote: async function (
    request: tCreateNoteRequest,
  ): Promise<tCreateNoteResponse> {
    try {
      const response = await AxiosService.post<
        tCreateNoteData,
        tCreateNoteResponse
      >(`/chats/chats/${request.chatId}/note`, request.data, {
        headers: {},
        isLegacy: true,
      });
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  updateTag: async function (
    request: tUpdateTagRequest,
    userId: string,
  ): Promise<tUpdateTagResponse> {
    try {
      const url = `/leads/ws/${request.workspaceId}/lds/${request.chatId}`;
      const config = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };

      const response = await AxiosService.put<tTagRequest, tUpdateTagResponse>(
        url,
        { attach_tags: request.attach_tags },
        config,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  deleteTag: async function (
    request: tDeleteTagRequest,
    userId: string,
  ): Promise<tUpdateTagResponse> {
    try {
      const url = `/leads/ws/${request.workspaceId}/lds/${request.chatId}`;
      const config = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };

      const response = await AxiosService.put<
        tTagDeleteRequest,
        tUpdateTagResponse
      >(url, { detach_tags: request.detach_tags }, config);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  deleteEvent: async function (
    request: tDeleteEventRequest,
    userId: string,
  ): Promise<tDeleteEventResponse> {
    try {
      const url = `/chats/events/${request.eventId}?workspace_id=${request.workspaceId}`;
      const config = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };
      const response = await AxiosService.delete<tDeleteEventResponse>(
        url,
        config,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  updateEvent: async function (
    request: tUpdateEventRequest,
    userId: string,
  ): Promise<tUpdateEventResponse> {
    try {
      const url = `/chats/events/${request.eventId}?workspace_id=${request.workspaceId}`;
      const config = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };
      const response = await AxiosService.put<
        tUpdateEventData,
        tUpdateEventResponse
      >(url, request.data, config);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  createFollowUp: async function (
    request: tCreateFollowUpRequest,
  ): Promise<tCreateFollowUpResponse> {
    try {
      const url = `/chats/chats/${request.chatId}/follow-up`;
      const config = {
        headers: {},
        isLegacy: true,
      };
      const response = await AxiosService.post<
        tCreateFollowUpData,
        tCreateFollowUpResponse
      >(url, request.data, config);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  createQuickAction: async function (
    chatId: string,
    action: string,
  ): Promise<void> {
    try {
      const url = `/chats/chats/${chatId}/quick-action`;
      const config = {
        headers: {},
        isLegacy: true,
      };
      await AxiosService.post(url, { action }, config);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  deleteChatEvent: async function (
    eventId: string,
    params: DeleteChatEventPayload,
  ): Promise<void> {
    try {
      const url = `/chats/events/${eventId}`;
      const config = {
        headers: {},
        params: params,
        isLegacy: true,
      };
      await AxiosService.delete(url, config);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  forwardEvent: async function (
    chatId: string,
    eventId: string,
    request: tForwardRequest,
  ): Promise<tForwardEventResponse | undefined> {
    try {
      const payload = {
        workspace_id: request.workspaceId,
        channel_type: request.channelType,
        to_chat_ids: request.toHandles,
        created_by: request.createdBy,
      };

      const url = `/chats/chats/${chatId}/forward-v2/${eventId}`;
      const config = {
        headers: {},
        isLegacy: true,
      };
      // Returned so the caller can read each destination's result: a
      // destination the outbound send gate refused only says so here.
      const response = await AxiosService.post<
        typeof payload,
        tForwardEventResponse | undefined
      >(url, payload, config);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getChatByWorkspaceAndLead: async function (
    workspaceId: string,
    leadId: string,
    integration: eChannelType,
  ): Promise<tChatData> {
    try {
      const url = `/workspace/${workspaceId}/chats?leadIDs=${leadId}&integration=${integration}`;
      const response = await AxiosService.get<tChatResponse>(url);
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getChatEventById: async function (
    eventId: string,
  ): Promise<tChatEventResponse<tChatEvent>> {
    try {
      const response = await AxiosService.get<tChatEventResponse<tChatEvent>>(
        `/event/${eventId}`,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getQuickReplies: async function (
    workspaceId: string,
    config?: tAxiosCustomConfig,
  ): Promise<tQuickReply[]> {
    try {
      // Rows may arrive with `interactive_config` at the top level (the
      // quick-replies-service wire shape from PR #45) because the existing
      // gateway transformer doesn't know about the new field. The rest of
      // the UI reads `content.interactive`, so hydrate that here — once —
      // and every downstream consumer (QuickActionRow, picker, pill) can
      // keep using the existing shape. Handles both snake_case
      // (`interactive_config`) and a defensive camelCase fallback in case
      // the gateway is updated to convert casing later.
      const response = await AxiosService.get<
        tChatEventResponse<{ quickReplies: tQuickReplyRaw[] }>
      >(`/workspace/${workspaceId}/quick-replies`, config);
      const hydrated = response.data.quickReplies.map(hydrateQuickReply);
      return Promise.resolve(hydrated);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  createQuickReply: async function (
    request: tQuickActionPayload,
  ): Promise<tQuickActionResponse> {
    try {
      const url = `/workspace/quick-replies`;
      const response = await AxiosService.post<
        tQuickActionPayload,
        tQuickActionResponse
      >(url, request);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  updateQuickReply: async function (
    request: tUpdateQuickActionPayload,
  ): Promise<tQuickActionResponse> {
    try {
      const url = `/workspace/quick-replies`;
      const response = await AxiosService.put<
        tUpdateQuickActionPayload,
        tQuickActionResponse
      >(url, request);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  deleteQuickReply: async function (
    workspaceId: string,
    id: string,
  ): Promise<tQuickActionResponse> {
    try {
      const url = `/workspace/${workspaceId}/quick-replies/${id}`;
      const response = await AxiosService.delete<tQuickActionResponse>(url);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getForwardLeads: async function (
    request: tChatLeadsRequest,
  ): Promise<tGetLeadsResponse> {
    try {
      let params = `page=${request.page}&integration=${request.integration}`;
      if (request.query) {
        params += `&query=${request.query}`;
      }

      const url = `/workspace/${request.workspaceId}/leads/forward?${params}`;
      const response = await AxiosService.get<tGetLeadsResponse>(url);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getChatID: async function ({
    workspaceId,
    leadId,
    integration,
    handle,
  }: {
    workspaceId: string;
    leadId: string;
    integration: string;
    handle: string;
  }): Promise<tGetChatIDResponse> {
    try {
      const response = await AxiosService.get<tGetChatIDResponse>(
        `/workspace/${workspaceId}/lead/${leadId}/chat?integration=${integration}&handle=${handle}`,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getLead: async function (
    {
      workspaceId,
      leadId,
    }: {
      workspaceId: string;
      leadId: string;
    },
    config?: tAxiosCustomConfig,
  ): Promise<tGetChatLeadResponse> {
    try {
      const response = await AxiosService.get<tGetChatLeadResponse>(
        `/workspace/${workspaceId}/lead/${leadId}/web`,
        config,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getChat: async function ({
    workspaceId,
    chatId,
  }: {
    workspaceId: string;
    leadId: string;
    chatId: string;
  }): Promise<tGetChatResponse> {
    try {
      const responseChatEvents = await AxiosService.get<tGetChatResponse>(
        `/workspace/${workspaceId}/chat/${chatId}`,
      );

      if (!responseChatEvents) {
        return Promise.reject('No chat events found');
      }

      return Promise.resolve(responseChatEvents);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getFavoriteMessages: async function (
    chatId: string,
    config?: tAxiosCustomConfig,
  ): Promise<tChatEvent[]> {
    try {
      const response = await AxiosService.get<
        tChatEventResponse<{ favoriteMessages: tChatEvent[] }>
      >(`/chat/${chatId}/favorite-messages`, config);
      return Promise.resolve(response.data.favoriteMessages);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getNotes: async function (
    chatId: string,
    config?: tAxiosCustomConfig,
  ): Promise<tChatEvent[]> {
    try {
      const response = await AxiosService.get<
        tChatEventResponse<{ notes: tChatEvent[] }>
      >(`/chat/${chatId}/notes`, config);
      return Promise.resolve(response.data.notes);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getLeadsalesAI: async function (
    chatId: string,
    config?: tAxiosCustomConfig,
  ): Promise<tChatEvent[]> {
    try {
      const response = await AxiosService.get<
        tChatEventResponse<{ leadsalesAI: tChatEvent[] }>
      >(`/chat/${chatId}/leadsales-ai`, config);
      return Promise.resolve(response.data.leadsalesAI);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getFollowUps: async function ({
    chatId,
    config,
  }: {
    chatId: string;
    config?: tAxiosCustomConfig;
  }): Promise<tChatEventResponse<{ followUps: tChatEvent[] }>> {
    try {
      const response = await AxiosService.get<
        tChatEventResponse<{ followUps: tChatEvent[] }>
      >(`/chat/${chatId}/follow-ups`, config);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getChatHistory: async function (
    request: tGetLeadHistoryRequest,
  ): Promise<tGetLeadHistoryResponse> {
    try {
      const response = await AxiosService.get<tGetLeadHistoryResponse>(
        `/workspace/${request.workspaceId}/lead/${request.leadId}/history`,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getNextPage: async function (request: tGetNextPageRequest) {
    try {
      const params = new URLSearchParams({ cursor: request.cursor });

      // Only sent when walking forward: the BFF validates `direction` as an
      // enum, and an empty value is not one of its two members.
      if (request.direction === 'forward') params.set('direction', 'forward');

      const response = await AxiosService.get<tGetNextPageResponse>(
        `/chat/${request.chatId}/events?${params.toString()}`,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * The page containing one specific event, for a message-search jump.
   *
   * Built with URLSearchParams rather than interpolation for the reason bff-web
   * moved off Sprintf for the same call: an opaque event id is not guaranteed
   * query-safe, and an unescaped one reads upstream as another separator and
   * truncates the anchor — which would come back as somebody else's page.
   */
  getEventsAround: async function (request: tGetEventsAroundRequest) {
    try {
      const params = new URLSearchParams({ around: request.eventId });

      const response = await AxiosService.get<tGetNextPageResponse>(
        `/chat/${request.chatId}/events?${params.toString()}`,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  sendMessage: async function (request: tSendMessageRequest) {
    try {
      const response = await AxiosService.post<object, tSendMessageResponse>(
        `/chats/chats/${request.chatId}/send`,
        request.data,
        {
          headers: {},
          isLegacy: true,
        },
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  sendAudioS3: async function (
    request: tUploadFileToS3Request,
  ): Promise<tPipeResponse<tUploadFileToS3ResponseData>> {
    try {
      const fileSize = request.file.size / 1024 / 1024;
      const mimeType = request.file.type;

      if (fileSize > 15) {
        throw new Error(
          `Audio file size (${fileSize.toFixed(2)}MB) exceeds the 15MB limit`,
        );
      }

      if (!mimeType.startsWith('audio/')) {
        throw new Error(
          `Invalid file type: ${mimeType}. Only audio files are allowed`,
        );
      }

      const url = `/storage/audio`;
      const formData = new FormData();
      formData.append('audio', request.file);

      const options = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          workspaceId: request.workspaceId,
          integration: request.integration,
        },
        isLegacy: true,
      };

      const response = await AxiosService.post<
        FormData,
        tPipeResponse<tUploadFileToS3ResponseData>
      >(url, formData, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // Sandbox-only uploaders. These write to the ephemeral `sandbox-media/<wid>/`
  // S3 prefix, which a bucket lifecycle rule expires after 7 days. Active
  // cleanup (reset / step-advance / unmount / pagehide) keeps the prefix near
  // empty in practice; the lifecycle rule is the safety net.
  sendSandboxImageS3: async function (request: {
    workspaceId: string;
    file: File;
  }): Promise<tPipeResponse<tUploadFileToS3ResponseData>> {
    try {
      const fileSize = request.file.size / 1024 / 1024;
      const mimeType = request.file.type;

      if (fileSize > 15) {
        throw new Error(
          `Image file size (${fileSize.toFixed(2)}MB) exceeds the 15MB limit`,
        );
      }

      if (!mimeType.startsWith('image/')) {
        throw new Error(
          `Invalid file type: ${mimeType}. Only image files are allowed`,
        );
      }

      const url = sandboxEndpoint('/storage/sandbox-image');
      const formData = new FormData();
      formData.append('image', request.file);

      const options = {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { workspaceId: request.workspaceId },
      };

      const response = await AxiosService.post<
        FormData,
        tPipeResponse<tUploadFileToS3ResponseData>
      >(url, formData, options);

      // Mask the raw S3 URL with our env-aware CDN domain so the bubble + the
      // multimodal payload to leads-ai-service use the public-facing host.
      if (response.data?.url) response.data.url = maskSandboxStorageUrl(response.data.url);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  sendSandboxAudioS3: async function (request: {
    workspaceId: string;
    file: File;
  }): Promise<tPipeResponse<tUploadFileToS3ResponseData>> {
    try {
      const fileSize = request.file.size / 1024 / 1024;
      const mimeType = request.file.type;

      if (fileSize > 15) {
        throw new Error(
          `Audio file size (${fileSize.toFixed(2)}MB) exceeds the 15MB limit`,
        );
      }

      if (!mimeType.startsWith('audio/')) {
        throw new Error(
          `Invalid file type: ${mimeType}. Only audio files are allowed`,
        );
      }

      const url = sandboxEndpoint('/storage/sandbox-audio');
      const formData = new FormData();
      formData.append('audio', request.file);

      const options = {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { workspaceId: request.workspaceId },
      };

      const response = await AxiosService.post<
        FormData,
        tPipeResponse<tUploadFileToS3ResponseData>
      >(url, formData, options);

      // Mask the raw S3 URL with our env-aware CDN domain so the bubble + the
      // multimodal payload to leads-ai-service use the public-facing host.
      if (response.data?.url) response.data.url = maskSandboxStorageUrl(response.data.url);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // Nukes everything under `sandbox-media/<workspaceId>/`. Idempotent — fine to
  // call on empty prefixes. Used by reset, step-advance, and unmount paths.
  deleteSandboxMedia: async function (workspaceId: string): Promise<void> {
    try {
      await AxiosService.delete(sandboxEndpoint(`/storage/sandbox-media/${workspaceId}`));
    } catch (error) {
      // Cleanup is best-effort — the 7-day lifecycle policy is the safety net.
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'ChatService.deleteSandboxMedia',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Failed to delete sandbox media',
        error as Error,
      );
    }
  },

  // Absolute URL for the BotSandbox hook's `pagehide` cleanup. Used with
  // `fetch(url, { method: 'DELETE', keepalive: true })` — `keepalive` survives
  // page unload while still supporting DELETE (the W3C Beacon API would be
  // simpler but it's POST-only). Absolute URL is required because relative
  // paths can be unreliable during unload.
  sandboxMediaBeaconUrl: function (workspaceId: string): string {
    return sandboxEndpoint(`/storage/sandbox-media/${workspaceId}`);
  },

  updateLead: async function (
    workspaceId: string,
    leadId: string,
    userId: string,
    request: tUpdateLeadRequest,
  ): Promise<tPipeResponse<tFunnelLeadLegacy>> {
    try {
      const options = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };
      const response = await AxiosService.put<
        tUpdateLeadRequest,
        tPipeResponse<tFunnelLeadLegacy>
      >(`/leads/ws/${workspaceId}/lds/${leadId}`, request, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // Individual lead archive
  archiveLead: async function (
    workspaceId: string,
    leadId: string,
  ): Promise<tArchiveResponse> {
    try {
      const response = await AxiosService.post<undefined, tArchiveResponse>(
        `/workspace/${workspaceId}/leads/${leadId}/archive`,
        undefined,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // Individual lead unarchive
  unarchiveLead: async function (
    workspaceId: string,
    leadId: string,
  ): Promise<tArchiveResponse> {
    try {
      const response = await AxiosService.post<undefined, tArchiveResponse>(
        `/workspace/${workspaceId}/leads/${leadId}/unarchive`,
        undefined,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  deleteFileS3: async function (request: tDeleteFileToS3Request) {
    try {
      const url = `/storage/files`;
      const options = {
        headers: {},
        params: {
          file: request.idFile,
        },
        isLegacy: true,
      };
      await AxiosService.deleteById(url, `${request.workspaceId}`, options);

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getTemplates: async function (
    chatId: string,
    config?: tAxiosCustomConfig,
  ): Promise<tChatEvent[]> {
    try {
      const url = `/chat/${chatId}/templates`;
      const response = await AxiosService.get<tGetTemplatesResponse>(url, config);

      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getTemplatesFbIg: async function (): Promise<TemplateFbIg[]> {
    try {
      const url = `/meta/templates`;
      const response = await AxiosService.get<tFetchTemplatesResponse>(url);

      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  changeChatState: async function (
    workspaceId: string,
    handle: string,
    request: tChangeChatStateRequest,
  ): Promise<tChangeChatState> {
    try {
      const url = `/meta-messenger/${workspaceId}/contact/${handle}/update-status`;
      const config = {
        headers: {},
        isLegacy: true,
      };
      const response = await AxiosService.post<
        tChangeChatStateRequest,
        tChangeChatState
      >(url, request, config);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getWorkspacesInfo: async function (
    workspaceId: string,
    funnelId: string,
    config?: tAxiosCustomConfig,
  ): Promise<tFunnelInfo> {
    try {
      const url = `/workspace/${workspaceId}/pipe/${funnelId}/info`;

      const response = await AxiosService.get<tGetWorkspacesInfoResponse>(
        url,
        config,
      );
      return Promise.resolve(response?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  scheduleWapiTemplate: async function (
    workspaceId: string,
    request: tWapiSendTemplateRequest & { trigger_date: string },
  ) {
    const config = {
      headers: {},
      isLegacy: true,
    };
    const url = `/whatsapp-api/meta/${workspaceId}/schedule/template`;
    const response = await AxiosService.post<
      tWapiSendTemplateRequest & { trigger_date: string },
      tWapiSendTemplateResponse
    >(url, request, config);
    return response;
  },
  sendWapiFollowTemplate: async function (
    workspaceId: string,
    request: tWapiSendTemplateRequest,
  ) {
    const config = {
      headers: {},
      isLegacy: true,
    };
    const url = `/whatsapp-api/meta/${workspaceId}/send/template`;
    const response = await AxiosService.post<
      tWapiSendTemplateRequest,
      tWapiSendTemplateResponse
    >(url, request, config);
    return response;
  },
  // Contact block/unblock on WhatsApp (INT-1294). Both go through bff-web
  // (`/whatsapp-api/workspaces/:wid/contacts/:handle/block`), which forwards
  // to whatsapp-business-service. Blocking is rejected with 422 and code
  // BLOCK_WINDOW_EXPIRED when the 24-hour conversation window is closed.
  blockContact: async function (
    workspaceId: string,
    handle: string,
  ): Promise<tBlockContactResponse> {
    const url = `/whatsapp-api/workspaces/${workspaceId}/contacts/${handle}/block`;
    return AxiosService.post<undefined, tBlockContactResponse>(url, undefined);
  },

  unblockContact: async function (
    workspaceId: string,
    handle: string,
  ): Promise<tBlockContactResponse> {
    const url = `/whatsapp-api/workspaces/${workspaceId}/contacts/${handle}/block`;
    return AxiosService.delete<tBlockContactResponse>(url);
  },

  // INT-1504: resend a WhatsApp API message Meta refused. bff-web forwards to
  // messaging's BUG-908 retry, which decides whether the message is safe to
  // resend: 404, 409, 422 (with a `reason`) and 423 are refusals, not outages.
  // On success the bubble moves to SENDING through the usual socket update.
  retryWapiMessage: async function (
    workspaceId: string,
    chatId: string,
    eventId: string,
  ): Promise<tRetryWapiMessageResponse> {
    const url = `/workspace/${workspaceId}/chat/${chatId}/messages/${eventId}/retry`;
    return AxiosService.post<undefined, tRetryWapiMessageResponse>(
      url,
      undefined,
    );
  },

  // INT-1537 / INT-1538 U9: tell messaging a person dealt with this chat's
  // undelivered failure, which clears its "!" mark. bff-web takes the user
  // from the token and the workspace from the path; the body only says why
  // and from where (INT-1602, analytics). 204 also when nothing was written.
  // Callers go through Chat.humanAction.ts, never here. A failure is
  // expected and harmless (the mark stays, the safe side), so it never
  // reaches Sentry.
  recordHumanAction: async function ({
    workspaceId,
    chatId,
    reason,
    surface,
  }: tRecordHumanActionParams): Promise<void> {
    const url = `/workspace/${workspaceId}/chat/${chatId}/human-action`;
    await AxiosService.post<tRecordHumanActionBody, void>(
      url,
      { reason, ...(surface && { surface }) },
      { skipSentryCapture: true },
    );
  },

  sendTypingIndicator: function (
    workspaceId: string,
    request: { messageId: string; handle: string },
  ) {
    const url = `/whatsapp-api/workspaces/${workspaceId}/typing-indicator`;
    AxiosService.post(url, request).catch(() => {});
  },
  // Interactive messages (Meta WAPI: Reply Buttons / CTA URL / List).
  // Posts to bff-web PR #643 (`/whatsapp-api/workspaces/:wid/messages/interactive`)
  // which passes through to WBS PR #611 (`/meta/:workspace_id/send/interactive`).
  // Payload is Meta wire shape — translate clean composer state via
  // toMetaWireInteractive() before calling this.
  sendWapiInteractive: async function (
    workspaceId: string,
    request: tWapiSendInteractiveRequest,
  ): Promise<tWapiSendInteractiveResponse> {
    // Default BASE_URL_V2 → bff-web (/v2/web/...). `isLegacy: true` points at
    // the legacy gateway which doesn't route /whatsapp-api/workspaces/:wid/
    // messages/interactive — it returned 404 in the initial staging send.
    // Matches the pattern used by sendReaction for the reaction sibling route.
    const url = `/whatsapp-api/workspaces/${workspaceId}/messages/interactive`;
    const response = await AxiosService.post<
      tWapiSendInteractiveRequest,
      tWapiSendInteractiveResponse
    >(url, request);
    return response;
  },

  createLeadsalesAI: async function (
    request: tCreateLeadsalesAIRequest,
  ): Promise<tCreateLeadsalesAIResponse> {
    const response = await AxiosService.post<
      tCreateLeadsalesAIData,
      tCreateLeadsalesAIResponse
    >(`/chats/chats/${request.chatId}/leadsales-ai`, request.data, {
      headers: {},
      isLegacy: true,
    });
    return response;
  },

  createSummaryAIEvent: async function (
    request: tCreateSummaryAIRequest,
  ): Promise<tCreateSummaryAIResponse> {
    try {
      // Uses the same endpoint as createLeadsalesAI but with different payload
      // Backend distinguishes summary events by leadsales_ai.type: 1 in the request data
      const response = await AxiosService.post<
        tCreateSummaryAIRequest['data'],
        tCreateSummaryAIResponse
      >(`/chats/chats/${request.chatId}/leadsales-ai`, request.data, {
        headers: {},
        isLegacy: true,
      });
      return response;
    } catch (error) {
      // Log error details in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to create summary AI event:', error);
      }

      // Re-throw error so callers can handle it appropriately
      throw error;
    }
  },

  // Summary functionality
  generateSummary: async function (
    chatId: string,
    request: tSummaryRequest,
  ): Promise<string> {
    try {
      const aiResponse = await summaryService.generateSummary(chatId, request);

      return aiResponse;
    } catch (aiError) {
      console.error('❌ ERROR IN AI SUMMARY SERVICE:', aiError);

      throw new Error('Error generating summary');
    }
  },
  checkSummaryEligibility: async function (
    request: tSummaryEligibilityRequest,
  ): Promise<tSummaryEligibilityResponse> {
    try {
      // First try real API
      const response = await AxiosService.get<tSummaryEligibilityResponse>(
        `/chat/${request.conversationId}/summary/eligibility`,
        {
          params: {
            timeframe_hours: request.timeframeHours || 24,
          },
        },
      );
      return Promise.resolve(response);
    } catch {
      // Fallback to mock eligibility check based on local chat data
      return ChatService.mockCheckSummaryEligibility();
    }
  },

  // Mock implementation for summary eligibility check (fallback)
  mockCheckSummaryEligibility:
    function (): Promise<tSummaryEligibilityResponse> {
      // This is a simple fallback that always returns not eligible
      // The real logic is now handled in Chat.hook.ts using selectedChat data
      return Promise.resolve({
        eligible: false,
        messageCount: 0,
        lastMessageTime: null,
        reason:
          'No hay datos de chat disponibles - usar lógica en Chat.hook.ts',
      });
    },

  getSummaryHistory: async function (
    chatId: string,
  ): Promise<tConversationSummary[]> {
    try {
      const response = await AxiosService.get<tConversationSummary[]>(
        `/chat/${chatId}/summaries`,
      );
      return Promise.resolve(response || []);
    } catch {
      return Promise.resolve([]);
    }
  },

  // Helper function to generate contextual summary from real messages
  generateContextualSummary: function (messages: tChatEvent[]): string {
    if (!messages || messages.length === 0) {
      LLog.warn(
          {
            component: LOG_COMPONENTS.UI,
            method: 'ChatService.generateContextualSummary',
            operation: LOG_OPERATIONS.ERROR,
          },
          'No hay mensajes disponibles para generar resumen.',
          { responseData: messages },
      );
      return `
        **Estado del Lead:** Sin mensajes recientes disponibles para generar resumen.

        **Información del Cliente:** No hay información suficiente en el período seleccionado.

        **Historial de Interacciones:** 
          - No se encontraron mensajes en las últimas 24 horas

        **Próximos Pasos:** Contactar al cliente para retomar la conversación.

        **Notas Importantes:** Revisar historial completo de la conversación para contexto adicional.
      `;
    }

    // Debug: log all messages first
    messages.forEach((msg, index) => {
      console.log(`📝 Message ${index}:`, {
        type: msg.type,
        hasContent: !!msg.content,
        hasText: !!msg.content?.text,
        text: msg.content?.text,
        hasMessage: !!msg.message,
        fromMe: msg.message?.fromMe,
        createdBy: msg.createdBy,
      });
    });

    // Extract message content and analyze
    const messageContents = messages
      .filter((msg) => {
        const hasContentText = msg.content?.text;
        console.log(
          `🔍 Filtering message - hasContentText: ${hasContentText}, text: "${msg.content?.text}"`,
        );
        return hasContentText;
      })
      .map((msg) => ({
        text: msg.content!.text!,
        isFromClient: !msg.message?.fromMe, // fromMe = false means from client
        timestamp: msg.createdAt,
        debug: {
          fromMe: msg.message?.fromMe,
          createdBy: msg.createdBy,
        },
      }))
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );

    console.log('📊 Processed messageContents:', messageContents);

    const clientMessages = messageContents.filter((msg) => msg.isFromClient);
    const agentMessages = messageContents.filter((msg) => !msg.isFromClient);

    // Analyze content keywords and sentiment
    const allText = messageContents.map((m) => m.text.toLowerCase()).join(' ');

    // Determine lead status based on content analysis
    let leadStatus = 'Cliente activo en conversación';
    let urgencyLevel = 'media';

    if (
      allText.includes('problema') ||
      allText.includes('molest') ||
      allText.includes('inacept')
    ) {
      leadStatus = 'Cliente con inconformidad que requiere atención inmediata';
      urgencyLevel = 'alta';
    } else if (
      allText.includes('interesad') ||
      allText.includes('comprar') ||
      allText.includes('precio')
    ) {
      leadStatus = 'Cliente interesado mostrando señales de compra';
      urgencyLevel = 'alta';
    } else if (allText.includes('informaci') || allText.includes('consulta')) {
      leadStatus = 'Cliente en fase de investigación';
      urgencyLevel = 'media';
    }

    // Extract key themes and issues
    const keyIssues: string[] = [];
    let clientInfo = 'Cliente participativo en la conversación';
    let nextSteps =
      'Continuar seguimiento según el contexto de la conversación';

    // Analyze specific issues mentioned
    if (allText.includes('don') && allText.includes('insuficient')) {
      keyIssues.push('Cliente reporta donas insuficientes');
    }
    if (allText.includes('compensa')) {
      keyIssues.push('Cliente solicita compensación por inconveniente');
      clientInfo = 'Cliente busca resolución a través de compensación';
    }
    if (
      allText.includes('parrilla') ||
      allText.includes('carne') ||
      allText.includes('vino')
    ) {
      keyIssues.push(
        'Cliente menciona compensación específica (parrilla de carne argentina y vino chileno)',
      );
    }
    if (allText.includes('abogado')) {
      keyIssues.push('Cliente menciona asistencia legal');
      urgencyLevel = 'muy alta';
      nextSteps =
        'Escalamiento inmediato a supervisor. Contactar dentro de las próximas 2 horas';
    }
    if (allText.includes('cancel')) {
      keyIssues.push('Cliente considera cancelación de servicio/plan');
      urgencyLevel = 'crítica';
    }

    // Build interactions history
    const interactionSummary = messageContents.slice(0, 5).map((msg) => {
      const source = msg.isFromClient ? 'Cliente' : 'Agente';
      const preview =
        msg.text.length > 60 ? msg.text.substring(0, 60) + '...' : msg.text;
      return `- ${source}: ${preview}`;
    });

    // Determine next steps based on urgency and content
    if (urgencyLevel === 'crítica' || urgencyLevel === 'muy alta') {
      nextSteps =
        'ACCIÓN INMEDIATA REQUERIDA: Contacto en las próximas 2 horas. Escalamiento a supervisor';
    } else if (urgencyLevel === 'alta') {
      nextSteps =
        'Seguimiento prioritario en las próximas 4-6 horas. Preparar propuesta de solución';
    } else {
      nextSteps = 'Seguimiento en 24-48 horas según cronograma estándar';
    }

    return `**Estado del Lead:** ${leadStatus}

**Información del Cliente:** ${clientInfo}. Ha enviado ${clientMessages.length} mensaje(s) y recibido ${agentMessages.length} respuesta(s) del agente.

**Historial de Interacciones:**
${interactionSummary.join('\n')}
${interactionSummary.length < messageContents.length ? `- ... y ${messageContents.length - interactionSummary.length} mensaje(s) adicional(es)` : ''}

**Temas Principales Identificados:**
${keyIssues.length > 0 ? keyIssues.map((issue) => `- ${issue}`).join('\n') : '- Conversación general sin temas específicos identificados'}

**Próximos Pasos:** ${nextSteps}

**Notas Importantes:** Urgencia ${urgencyLevel}. Total de ${messages.length} evento(s) analizados en las últimas 24 horas. ${urgencyLevel === 'crítica' || urgencyLevel === 'muy alta' ? '⚠️ REQUIERE ATENCIÓN INMEDIATA' : 'Seguimiento según protocolo estándar'}.`;
  },

  getParticipants: async function (
    chatId: string,
    config?: tAxiosCustomConfig,
  ) {
    try {
      const url = `/chat/${chatId}/groupMembers/contacts`;
      const response = await AxiosService.get<tGetParticipants>(url, config);

      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
